import * as React from "react";
import { Badge, Button, TypographyPSmall, cn } from "@finster/ui";
import { Check, ImageOff, Maximize2, Star, Trash2 } from "lucide-react";

import type { SlideLibrarySlide } from "../../be/types";
import type { SlideDropPosition, SlideTileStatus } from "../dialog/types";
import { buildApiUrl } from "../../../config";
import {
  AnimatedSpinner,
  FadeInThumbnail,
  ProcessingThumbnail,
  SelectionCheck,
  staggerStyle,
} from "./motion";

function buildThumbnailSrc(storagePath: string): string {
  if (storagePath.startsWith("data:")) {
    return storagePath;
  }
  if (storagePath.startsWith("/")) {
    return buildApiUrl(storagePath);
  }
  if (/^https?:\/\//i.test(storagePath)) {
    try {
      const url = new URL(storagePath);
      if (typeof window !== "undefined" && url.origin === window.location.origin) {
        return `${url.pathname}${url.search}`;
      }
    } catch {
      // Fall through to the authenticated proxy.
    }
    return buildApiUrl(`/api/gcpfile/${encodeURIComponent(storagePath)}`);
  }
  // Slide library assets live in private GCS buckets (or as relative storage
  // keys). The browser cannot load those URLs directly; proxy via gcpfile.
  return buildApiUrl(`/api/gcpfile/${encodeURIComponent(storagePath)}`);
}

export function buildLibraryThumbnailSrc(slide: SlideLibrarySlide): string | null {
  const storagePath =
    slide.localThumbnailDataUri ||
    slide.thumbnail_url ||
    slide.thumbnailUrl ||
    slide.thumbnail_storage_path ||
    slide.thumbnailStoragePath ||
    slide.thumbnail_path ||
    slide.thumbnailPath ||
    slide.thumbnail_gcs_url ||
    slide.thumbnailGcsUrl ||
    slide.thumbnail_download_url ||
    slide.thumbnailDownloadUrl;
  if (!storagePath) {
    return null;
  }
  return buildThumbnailSrc(storagePath);
}

export function isLibrarySlideProcessing(slide: SlideLibrarySlide): boolean {
  const status = slide.status.toUpperCase();
  if (["READY", "ERROR", "FAILED", "MALICIOUS"].includes(status)) {
    return false;
  }
  return true;
}

function isPlaceholderSlideTitle(title: string | undefined): boolean {
  const normalized = (title || "").trim().toLowerCase();
  return normalized.length === 0 || normalized === "untitled slide";
}

export function getLibrarySlideLabel(slide: SlideLibrarySlide): string {
  const primaryTag = slide.tags[0]?.trim();
  if (primaryTag) {
    return primaryTag;
  }
  if (!isPlaceholderSlideTitle(slide.title)) {
    return slide.title.trim();
  }
  return "Slide";
}

function formatStatusLabel(status: string): string {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getLibrarySlideTileStatus(slide: SlideLibrarySlide): SlideTileStatus {
  const rawStatus = slide.status.trim();
  const status = rawStatus.toUpperCase();
  if (["ERROR", "FAILED", "MALICIOUS"].includes(status)) {
    return {
      label: status === "MALICIOUS" ? "Scan failed" : "Processing failed",
      tone: "error",
      busy: false,
    };
  }
  if (status === "READY") {
    return { label: "Ready", tone: "success", busy: false };
  }
  if (!status) {
    return { label: "Processing", tone: "info", busy: true };
  }
  return {
    label: formatStatusLabel(rawStatus),
    tone: "info",
    busy: true,
  };
}

export function SlideTileStatusPill({ status }: { status: SlideTileStatus }) {
  return (
    <span
      className={cn(
        "inline-flex h-6 max-w-full shrink-0 items-center gap-1 rounded-full border px-2",
        "text-[10px] font-medium leading-none shadow-sm backdrop-blur-sm",
        status.tone === "error" && "border-destructive/20 bg-destructive/10 text-destructive",
        status.tone === "success" && "border-emerald-500/20 bg-emerald-500/10 text-emerald-700",
        status.tone === "info" && "border-primary/20 bg-primary/10 text-primary",
        status.tone === "muted" && "border-border/70 bg-background/85 text-muted-foreground"
      )}
    >
      {status.busy ? <AnimatedSpinner className="h-2.5 w-2.5" /> : null}
      {status.tone === "success" ? <Check className="h-2.5 w-2.5" /> : null}
      <span className="truncate">{status.label}</span>
    </span>
  );
}

function EmptySlideThumbnailState({ status }: { status: SlideTileStatus }) {
  const isError = status.tone === "error";
  const title = isError
    ? "Preview unavailable"
    : status.tone === "muted"
      ? "No preview"
      : status.label;
  const detail = isError ? status.label : status.tone === "muted" ? "Thumbnail has not been generated." : "Working on this slide.";

  return (
    <div
      className={cn(
        "relative flex h-full flex-col items-center justify-center overflow-hidden rounded-lg p-4 text-center",
        isError ? "bg-destructive/5" : "bg-background"
      )}
    >
      <div
        className={cn(
          "mb-3 flex h-10 w-10 items-center justify-center rounded-full border shadow-sm",
          isError
            ? "border-destructive/20 bg-destructive/10 text-destructive"
            : "border-border bg-background-secondary text-muted-foreground"
        )}
      >
        {status.busy ? <AnimatedSpinner className="h-4 w-4" /> : <ImageOff className="h-4 w-4" />}
      </div>
      <TypographyPSmall
        className={cn(
          "font-medium [&:not(:last-child)]:mb-0",
          isError ? "text-destructive" : "text-foreground"
        )}
      >
        {title}
      </TypographyPSmall>
      <TypographyPSmall className="mt-1 text-[11px] text-muted-foreground [&:not(:last-child)]:mb-0">
        {detail}
      </TypographyPSmall>
    </div>
  );
}

interface LibrarySlideCardProps {
  slide: SlideLibrarySlide;
  index: number;
  isSelectable: boolean;
  isSelected: boolean;
  isViewingCollection: boolean;
  isDeleting: boolean;
  isRemoving: boolean;
  isFavouriting: boolean;
  isUpdatingTag: boolean;
  isDraggable?: boolean;
  isDragging?: boolean;
  isDropSettling?: boolean;
  dragOverPosition?: SlideDropPosition | null;
  hideDestructiveAction?: boolean;
  onToggleSelection?: (slide: SlideLibrarySlide) => void;
  onToggleFavourite: (slide: SlideLibrarySlide) => void;
  onPreviewSlide: (slide: SlideLibrarySlide) => void;
  onOpenTagPicker: (slide: SlideLibrarySlide) => void;
  onDeleteOrRemove: (slide: SlideLibrarySlide) => void;
  onDragStart?: (event: React.DragEvent<HTMLElement>, slide: SlideLibrarySlide) => void;
  onDragOver?: (event: React.DragEvent<HTMLElement>, slide: SlideLibrarySlide) => void;
  onDragLeave?: (event: React.DragEvent<HTMLElement>, slide: SlideLibrarySlide) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>, slide: SlideLibrarySlide) => void;
  onDragEnd?: (slide: SlideLibrarySlide) => void;
}

export function LibrarySlideCard({
  slide,
  index,
  isSelectable,
  isSelected,
  isViewingCollection,
  isDeleting,
  isRemoving,
  isFavouriting,
  isUpdatingTag,
  isDraggable = false,
  isDragging = false,
  isDropSettling = false,
  dragOverPosition = null,
  hideDestructiveAction = false,
  onToggleSelection,
  onToggleFavourite,
  onPreviewSlide,
  onOpenTagPicker,
  onDeleteOrRemove,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: LibrarySlideCardProps) {
  const thumbnailSrc = buildLibraryThumbnailSrc(slide);
  const slideLabel = getLibrarySlideLabel(slide);
  const isProcessing = isLibrarySlideProcessing(slide);
  // Allow selecting processing/stuck slides so users can bulk-delete them.
  // Insert still filters to READY via selectedInsertableLibrarySlides.
  const canSelect = isSelectable;
  const canPreview = Boolean(thumbnailSrc) && !isProcessing;
  const tileStatus = getLibrarySlideTileStatus(slide);
  const emptyThumbnailStatus =
    tileStatus.tone === "success"
      ? { label: "No preview", tone: "muted" as const, busy: false }
      : tileStatus;
  const showHeaderStatus = Boolean(thumbnailSrc) && tileStatus.tone !== "success";

  return (
    <article
      style={staggerStyle(index)}
      data-selected={canSelect && isSelected ? "true" : "false"}
      data-unselectable={isSelectable && !canSelect ? "true" : "false"}
      data-dragging={isDragging ? "true" : "false"}
      data-drop-target={dragOverPosition ? "true" : "false"}
      data-drop-settling={isDropSettling ? "true" : "false"}
      role={canSelect ? "button" : undefined}
      tabIndex={canSelect ? 0 : undefined}
      aria-pressed={canSelect ? isSelected : undefined}
      aria-disabled={isSelectable && !canSelect ? true : undefined}
      aria-label={canSelect ? `${isSelected ? "Deselect" : "Select"} ${slideLabel}` : undefined}
      onClick={canSelect && onToggleSelection ? () => onToggleSelection(slide) : undefined}
      draggable={isDraggable}
      onDragStart={isDraggable && onDragStart ? (event) => onDragStart(event, slide) : undefined}
      onDragOver={isDraggable && onDragOver ? (event) => onDragOver(event, slide) : undefined}
      onDragLeave={isDraggable && onDragLeave ? (event) => onDragLeave(event, slide) : undefined}
      onDrop={isDraggable && onDrop ? (event) => onDrop(event, slide) : undefined}
      onDragEnd={isDraggable && onDragEnd ? () => onDragEnd(slide) : undefined}
      onKeyDown={
        canSelect && onToggleSelection
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onToggleSelection(slide);
              }
            }
          : undefined
      }
      className={cn(
        "sl-slide-card sl-stagger-item sl-card-surface relative box-border rounded-lg border border-transparent bg-background p-1.5",
        "[contain:layout]",
        canSelect && "sl-interactive cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        isSelectable && !canSelect && "cursor-default",
        canSelect && isSelected && "shadow-sm",
        isDraggable && "cursor-grab active:cursor-grabbing",
        (isDragging || dragOverPosition || isDropSettling) && "will-change-transform",
        isDragging && "z-20"
      )}
    >
      {dragOverPosition ? (
        <div
          className={cn(
            "sl-drop-indicator pointer-events-none absolute inset-y-2 z-20 w-1 rounded-full bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.12)]",
            dragOverPosition === "before" ? "left-0" : "right-0"
          )}
          aria-hidden="true"
        />
      ) : null}
      {isDragging ? (
        <div
          className={cn(
            "sl-drag-state-badge pointer-events-none absolute left-1/2 top-1/2 z-30",
            "flex -translate-x-1/2 -translate-y-1/2 items-center rounded-full",
            "border border-primary/25 bg-background/95 px-2.5 py-1 text-[10px] font-medium",
            "text-primary shadow-lg backdrop-blur-sm"
          )}
          aria-hidden="true"
        >
          Moving slide
        </div>
      ) : null}
      <div className="mb-1 flex min-h-7 items-start justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {canSelect ? (
            <SelectionCheck selected={isSelected} className="sl-selection-indicator" />
          ) : null}
          {showHeaderStatus ? <SlideTileStatusPill status={tileStatus} /> : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn(
            "sl-card-action sl-favorite-button h-6 w-6 shrink-0 rounded-full bg-background",
            "text-muted-foreground hover:bg-background hover:text-amber-500",
            slide.favourite && "sl-card-action-visible bg-amber-50 text-amber-500 hover:text-amber-500"
          )}
          disabled={isFavouriting}
          aria-label={slide.favourite ? "Remove from favorites" : "Add to favorites"}
          onClick={(event) => {
            event.stopPropagation();
            onToggleFavourite(slide);
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {isFavouriting ? (
            <AnimatedSpinner className="h-3 w-3" />
          ) : (
            <Star className={cn("h-3.5 w-3.5 transition-transform", slide.favourite && "fill-current")} />
          )}
        </Button>
      </div>
      <div className="sl-card-media overflow-hidden rounded-md bg-background shadow-[0_1px_2px_hsl(var(--foreground)/0.05)]">
        <div className="relative aspect-[16/9]">
          {thumbnailSrc ? (
            <>
              <FadeInThumbnail src={thumbnailSrc} alt={slideLabel} />
            </>
          ) : isProcessing ? (
            <ProcessingThumbnail label={tileStatus.label} />
          ) : (
            <EmptySlideThumbnailState status={emptyThumbnailStatus} />
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between gap-1">
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          disabled={isUpdatingTag}
          onClick={(event) => {
            event.stopPropagation();
            onOpenTagPicker(slide);
          }}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Badge
            shape="round"
            variant="outline"
            className="max-w-full truncate border-border bg-background text-[10px] font-normal leading-4 text-muted-foreground hover:bg-accent/30"
          >
            {isUpdatingTag ? "Updating..." : slideLabel}
          </Badge>
        </button>
        {canPreview ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="sl-card-action h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={`Preview ${slideLabel}`}
            onClick={(event) => {
              event.stopPropagation();
              onPreviewSlide(slide);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Maximize2 className="h-3 w-3" />
          </Button>
        ) : null}
        {!hideDestructiveAction ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={cn(
              "sl-card-action h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive",
              (isDeleting || isRemoving) && "sl-card-action-visible"
            )}
            disabled={isDeleting || isRemoving}
            aria-label={
              isViewingCollection ? `Remove ${slideLabel} from collection` : `Delete ${slideLabel}`
            }
            onClick={(event) => {
              event.stopPropagation();
              onDeleteOrRemove(slide);
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {isDeleting || isRemoving ? (
              <AnimatedSpinner className="h-3 w-3" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
          </Button>
        ) : null}
      </div>
    </article>
  );
}
